

import { TextInput, TertiaryButton, CancelFailLineIcon } from '@tricor-digital-team/vistra-design-system';
import React from 'react';
import { z } from 'zod';


const PersonalDetails = ({
  personalDetails,
  setPersonalDetails,
  index,
  removeIndividual,
  personalDetailsErrors,
  setPersonalDetailsErrors,
  selectedIndividual,
  isEditSummary,
  existingEmails,
  currentLength,
  fieldRefs,
  setIsError
}) => {

  const isEmailUniqueInList = (email, indexToIgnore = index) => {
    const lowerEmail = email.toLowerCase().trim();
    return !existingEmails?.some((item, index) => {
      if (index === indexToIgnore) return false;
      return item.toLowerCase().trim() === lowerEmail;
    });
  }

  const personalDetailsSchema = z.object({
    firstName: z
      .string()
      .trim()
      .min(1, 'First name is required'), // trims and ensures non-empty
    lastName: z
      .string()
      .trim()
      .min(1, 'Last name is required'),
    middleName: z
      .string()
      .trim()
      .optional(),
    email: z
      .string()
      .email('Invalid email address')
      .refine((val) => isEmailUniqueInList(val), {
        message: 'Email must be unique',
      }),
  });

  const handleChange = (value, name) => {

    const newData = { ...personalDetails, [name]: value };
    setPersonalDetails(() => newData);
    const validation = personalDetailsSchema.shape[name]?.safeParse(value);

    const error = validation?.error?.format()?._errors?.[0]
    // dont show invalid email address early
    if ((name === 'email') && !validation?.success && !value.includes(' ') && error === 'Invalid email address') {
      return;
    }

    setPersonalDetailsErrors((prevErrors) => {
      const newErrors = {
        ...prevErrors,
        [index]: {
          ...prevErrors[index],
          [name]: validation?.success ? '' : error || 'Invalid',
        },
      };

      // Flatten and check if any error remains
      const hasAnyError = Object.values(newErrors).some(fieldGroup =>
        Object.values(fieldGroup).some(val => val)
      );

      setIsError(hasAnyError);
      return newErrors;
    });

  };

  return (
    <>
      <div className="flex justify-between items-center mt-4">
        <div className="text-black text-2xl font-normal">
          {selectedIndividual ? 'Edit Individual details' : 'Individual details ' + `${index + 1}`.padStart(2, '0')}
        </div>
        {!selectedIndividual && currentLength !== 1 ? <TertiaryButton
          icon={CancelFailLineIcon}
          iconPosition="left"
          label="Remove"
          onClick={() => removeIndividual(index)}
          color="red"
          size="medium"
        /> : null}
      </div>

      {isEditSummary && (<>
        <div className="text-black text-xl font-large mt-6">
          Change summary
        </div>
        <div className="text-sm text-gray-900 mb-2 mt-2">
          Check the detail below before you confirm the changes.
        </div>
      </>
      )}


      {selectedIndividual && (
        <div className="mt-6">
          <div className="text-black text-xl font-normal">Current detail</div>
          <div className="flex flex-col md:flex-row gap-6 mt-6">
            <div className="w-full">
              <div className="text-sm text-gray-400 mb-2">First name</div>
              <div className="text-base text-black mb-2">{selectedIndividual?.firstName || '-'}</div>
            </div>
            <div className="w-full">
              <div className="text-sm text-gray-400 mb-2">Middle name (optional)</div>
              <div className="text-base text-black mb-2">{selectedIndividual?.middleName || '-'}</div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-6 mt-6">
            <div className="w-full">
              <div className="text-sm text-gray-400 mb-2">Last name</div>
              <div className="text-base text-black mb-2">{selectedIndividual?.lastName || '-'}</div>
            </div>
            <div className="w-full">
              <div className="text-sm text-gray-400 mb-2">Email</div>
              <div className="text-base text-black mb-2">{selectedIndividual?.email || '-'}</div>
            </div>
          </div>
        </div>
      )}

      {(selectedIndividual || isEditSummary) && (
        <div className="mt-6">
          <div className="text-black text-xl font-normal">New detail</div>
        </div>
      )}
      {(!isEditSummary ? <>
        <div className="mt-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div ref={(el) => fieldRefs && (fieldRefs.current[`firstName-${index}`] = el)} className="w-full md:w-1/2">
              <TextInput
                id="firstName"
                labelText="First name"
                placeholder="Enter a first name"
                defaultValue={personalDetails?.firstName}
                errorText={personalDetailsErrors?.firstName}
                isInvalid={!!personalDetailsErrors?.firstName}
                onChange={(e) => handleChange(e.target.value, 'firstName')}
              />
            </div>
            <div ref={(el) => fieldRefs && (fieldRefs.current[`middleName-${index}`] = el)} className="w-full md:w-1/2">
              <TextInput
                id="middleName"
                labelText="Middle name (optional)"
                defaultValue={personalDetails?.middleName}
                onChange={(e) => handleChange(e.target.value, 'middleName')}
              />
            </div>
          </div>
        </div>
        <div className="mt-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div ref={(el) => fieldRefs && (fieldRefs.current[`lastName-${index}`] = el)} className="w-full md:w-1/2">
              <TextInput
                id="lastName"
                placeholder="Enter a last name"
                labelText="Last name"
                defaultValue={personalDetails?.lastName}
                errorText={personalDetailsErrors?.lastName}
                isInvalid={!!personalDetailsErrors?.lastName}
                onChange={(e) => handleChange(e.target.value, 'lastName')}
              />
            </div>
            <div ref={(el) => fieldRefs && (fieldRefs.current[`email-${index}`] = el)} className="w-full md:w-1/2">

              <TextInput
                id="email"
                labelText="Email"
                placeholder="e.g., abc@email.com"
                defaultValue={personalDetails?.email}
                hintText="This email must be a unique, individual email address."
                errorText={personalDetailsErrors?.email}
                isInvalid={!!personalDetailsErrors?.email}
                onChange={(e) => handleChange(e.target.value, 'email')}
              />
            </div>
          </div>
        </div>
      </> : <div>
        <div className="flex flex-col md:flex-row gap-6 mt-6">
          <div className="w-full">
            <div className="text-sm text-gray-400 mb-2">First name</div>
            <div className="text-base text-black mb-2">{personalDetails?.firstName || '-'}</div>
          </div>
          <div className="w-full">
            <div className="text-sm text-gray-400 mb-2">Middle name (optional)</div>
            <div className="text-base text-black mb-2">{personalDetails?.middleName || '-'}</div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-6 mt-6">
          <div className="w-full">
            <div className="text-sm text-gray-400 mb-2">Last name</div>
            <div className="text-base text-black mb-2">{personalDetails?.lastName || '-'}</div>
          </div>
          <div className="w-full">
            <div className="text-sm text-gray-400 mb-2">Email</div>
            <div className="text-base text-black mb-2">{personalDetails?.email || '-'}</div>
          </div>
        </div>
      </div>
      )}
    </>
  );
};

export default PersonalDetails;
